import React, {
  Fragment,
  KeyboardEvent,
  RefObject,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { EllipsisHorizontalIcon as DotsHorizontal } from '@heroicons/react/24/outline';
import { css } from '@emotion/react';
import { Combobox, Transition } from '@headlessui/react';
import { inputRootStyle } from '@teetsh/app/src/components/Form/Input2';
import { menuItemStyle } from '@teetsh/app/src/components/Menu/MenuItem';
import { menuItemsStyle } from '@teetsh/app/src/components/Menu/MenuItems';
import { OptionConfiguration } from '@teetsh/app/src/components/NewCustomFields/OptionConfiguration';
import Popover from '@teetsh/app/src/components/Popover';
import { Tag } from '@teetsh/app/src/components/Tag';
import tw from 'twin.macro';
import { Option } from './types';
import {
  autoUpdate,
  flip,
  offset,
  shift,
  size,
  useFloating,
} from '@floating-ui/react-dom';
import { useTranslation } from 'react-i18next';
import { useColorPalette } from 'containers/App/context/useColorPalette';
import { randomBackgroundColor } from 'components/colors';
import { TagColorWithIntensity } from '@teetsh/app/src/styles/userConstants';
import { Color } from 'types/colors';

interface Props {
  className?: string;
  options: Option[];
  inputRef?: RefObject<HTMLInputElement>;
  value: string[];
  allowMultipleValue: boolean;
  onChange: (v?: string[]) => void;
  onUpdateOption: (v: Option) => void;
  onRemoveOption: (v: string) => void;
  onCreateOption?: ({
    optionValue,
    color,
  }: {
    optionValue: string;
    color?: Color | TagColorWithIntensity;
  }) => void;
  name: string;
  id?: string;
  onClickOutside?: (event: any) => void;
  openOnMount?: boolean;
}

interface BodyProps {
  allowMultipleValue: boolean;
  inputRef?: RefObject<HTMLInputElement>;
  value: string[];
  onChange: (v?: string[]) => void;
  options: Option[];
  setQuery: (v: string) => void;
  floatingStyles: any;
  query: string;
  open: boolean;
  setIsOpen: (v: boolean) => void;
  refs: any;
  id?: string;
  onUpdateOption: (v: Option) => void;
  onRemoveOption: (v: string) => void;
  onClickOutside?: (event: any) => void;
  onCreateOption?: ({
    optionValue,
    color,
  }: {
    optionValue: string;
    color?: TagColorWithIntensity;
  }) => void;
  name: string;
  openOnMount?: boolean;
}

function SelectCustomFieldBody(props: BodyProps) {
  const {
    value,
    allowMultipleValue,
    open,
    onChange,
    setQuery,
    floatingStyles,
    options,
    onCreateOption,
    onUpdateOption,
    onRemoveOption,
    onClickOutside,
    name,
    query,
    openOnMount,
    refs,
  } = props;
  const { t } = useTranslation(['common']);
  const { colorPalette } = useColorPalette(); // this should not be called here (in components) but this will be cleaned up after July so let's say this is ok
  const [isOptionsOpen, setIsOptionsOpen] = useState(false);
  const defaultInputRef = React.useRef(null);
  const inputRef = props.inputRef || defaultInputRef;

  const newOptionColor = useMemo(() => randomBackgroundColor(), [options]);
  const filteredOption =
    query === ''
      ? options
      : options.filter(opt =>
          opt.value
            .toLowerCase()
            .replace(/\s+/g, '')
            .includes(query.toLowerCase().replace(/\s+/g, '')),
        );

  const showCreateOption = filteredOption.length === 0 && query !== '';

  const handleClickOutside = event => {
    const optionsPanel = document.getElementById('options-panel');
    const deletePanelWarning = document.getElementById('delete-panel-warning');
    const selectCustomFieldOptions = document.getElementById(
      'select-custom-field-options',
    );

    if (
      refs.floating &&
      refs.floating.current &&
      !optionsPanel?.contains(event.target) &&
      !deletePanelWarning?.contains(event.target) &&
      !selectCustomFieldOptions?.contains(event.target)
    ) {
      if (!refs.floating.current.contains(event.target)) {
        props.setIsOpen(false);
        if (onClickOutside) {
          return onClickOutside(event);
        }
      }
    }
  };
  useEffect(() => {
    if (open) {
      document.addEventListener('mousedown', handleClickOutside);

      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
    return;
  });

  useEffect(() => {
    if (inputRef?.current && openOnMount) {
      (inputRef.current as any)?.click();
    }
  }, []);

  return (
    <>
      <div tw="relative flex-grow">
        <Combobox.Button tw="w-full">
          <Combobox.Input
            aria-label={`Select Custom Field ${name}`}
            placeholder={
              (value || []).length > 0 && allowMultipleValue ? '...' : ''
            }
            css={[
              tw`border-none bg-transparent pt-0! pb-0! outline-none! focus:outline-none! pl-0! w-full leading-5 text-xs h-auto!`,
              css`
                box-shadow: none !important;
              `,
            ]}
            autoComplete="off"
            ref={inputRef}
            id={props.id}
            onClick={() => props.setIsOpen(true)}
            onKeyDown={(e: KeyboardEvent<any>) => {
              if (e.key === 'Backspace' && !!value && query === '') {
                onChange(value.slice(0, -1));
              }
              if (e.key === 'Enter') {
                if (onCreateOption && showCreateOption) {
                  onCreateOption({
                    optionValue: query,
                    color: newOptionColor,
                  });
                  setQuery('');
                }
              }
            }}
            displayValue={() => query}
            onChange={event => setQuery(event.target.value)}
          />
        </Combobox.Button>
      </div>
      <div tw="relative">
        <div
          tw="bg-white"
          ref={refs.setFloating}
          style={{
            ...floatingStyles,
            zIndex: 50,
          }}
        >
          {/*@ts-ignore*/}
          <Transition
            show={isOptionsOpen ? true : open && !isOptionsOpen}
            as={Fragment as any}
            leave="transition ease-in duration-100"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
            afterLeave={() => {
              setQuery('');
            }}
            id="select-custom-field-options"
          >
            <Combobox.Options
              static
              css={[
                tw`z-30 mt-0 max-h-60 py-1 text-base focus:outline-none sm:text-sm`,
                menuItemsStyle,
                tw`overflow-auto`, // menuItemsStyle defines overflow-hidden so we have to override it
              ]}
            >
              {options.length === 0 && query === '' && (
                <div
                  css={[
                    menuItemStyle({}),
                    tw`cursor-pointer select-none relative py-2 pl-3 pr-3 focus:outline-none`,
                  ]}
                >
                  {t('common:components.customFields.typeText')}
                </div>
              )}
              {showCreateOption ? (
                <div
                  onClick={() => {
                    if (onCreateOption) {
                      onCreateOption({
                        optionValue: query,
                        color: newOptionColor,
                      });
                      setQuery('');
                    }
                  }}
                  css={[
                    menuItemStyle({}),
                    tw`cursor-pointer select-none relative py-2 pl-3 pr-3 focus:outline-none`,
                  ]}
                >
                  {t('common:components.customFields.createItem')}
                  <Tag color={newOptionColor}>{query}</Tag>
                </div>
              ) : (
                filteredOption.map(opt => (
                  <Combobox.Option key={opt.id} value={opt}>
                    {({ active }) => (
                      <div
                        className="group"
                        css={[
                          menuItemStyle({ active }),
                          tw`cursor-pointer select-none relative py-2 pl-3 pr-3 focus:outline-none`,
                        ]}
                      >
                        <span tw="font-normal block truncate">
                          <Tag color={opt.color}>{opt.value}</Tag>
                        </span>
                        <span
                          onClick={e => {
                            e.preventDefault();
                            e.stopPropagation();
                          }}
                          tw="absolute top-0 bottom-0 right-0 flex items-center pr-4"
                        >
                          <Popover portal>
                            <Popover.Button
                              minimal
                              icon={DotsHorizontal}
                              aria-label={`Options de ${opt.value}`}
                            />
                            <Popover.Panel
                              size="auto"
                              onOpen={() => setIsOptionsOpen(true)}
                              onClose={() => setIsOptionsOpen(false)}
                            >
                              <div
                                id="options-panel"
                                css={[
                                  'max-height: 35vh;',
                                  tw`overflow-auto p-4`,
                                ]}
                              >
                                <OptionConfiguration
                                  onRemove={id => {
                                    setIsOptionsOpen(false);
                                    onRemoveOption(id);
                                  }}
                                  onChange={onUpdateOption}
                                  value={opt}
                                  colorPalette={colorPalette}
                                />
                              </div>
                            </Popover.Panel>
                          </Popover>
                        </span>
                      </div>
                    )}
                  </Combobox.Option>
                ))
              )}
            </Combobox.Options>
          </Transition>
        </div>
      </div>
    </>
  );
}

export function SelectCustomField(props: Props) {
  const {
    value,
    allowMultipleValue,
    onChange,
    options = [],
    inputRef,
    onCreateOption,
    onUpdateOption,
    onRemoveOption,
    onClickOutside,
    name,
    className,
  } = props;
  const [isOpen, setIsOpen] = useState(false);
  const { refs, floatingStyles } = useFloating({
    middleware: [
      offset(2),
      flip(),
      shift(),
      size({
        apply({ rects, elements }) {
          Object.assign(elements.floating.style, {
            width: `${rects.reference.width}px`,
          });
        },
      }),
    ],
    whileElementsMounted: autoUpdate,
  });

  const [query, setQuery] = useState('');

  const matchingOptions = value.reduce((acc, v) => {
    const matchingOption = options.find(opt => opt.id === v);
    if (!matchingOption) {
      return acc;
    }
    return [...acc, matchingOption];
  }, []);

  return (
    <div
      ref={refs.setReference}
      css={[
        tw`flex! flex-wrap bg-white px-2 gap-x-1 gap-y-1 h-auto!`,
        inputRootStyle({}),
      ]}
      className={className}
    >
      {matchingOptions.map(matchingOption => (
        <Tag
          tw="self-center gap-x-0.5"
          color={matchingOption.color}
          key={matchingOption.id}
        >
          {matchingOption.value}
          <button
            type="button"
            className="group"
            tw="relative -mr-1 h-3.5 w-3.5 rounded-full hover:bg-neutral-quiet/50"
            onClick={e => {
              onChange(value.filter(myv => myv !== matchingOption.id));
              e.stopPropagation();
            }}
          >
            <svg
              viewBox="0 0 14 14"
              stroke="currentColor"
              tw="h-3.5 w-3.5 group-hover:scale-110"
            >
              <path d="M4 4l6 6m0-6l-6 6" />
            </svg>
            <span tw="absolute -inset-1" />
          </button>
        </Tag>
      ))}
      <Combobox
        value={matchingOptions}
        multiple
        onChange={v => {
          setQuery('');
          onChange(
            allowMultipleValue
              ? v.map(opt => opt.id)
              : v
                  .filter(opt => !(value || []).includes(opt.id))
                  .map(opt => opt.id),
          );
        }}
      >
        <SelectCustomFieldBody
          value={value}
          allowMultipleValue={allowMultipleValue}
          onChange={onChange}
          open={isOpen}
          setIsOpen={setIsOpen}
          inputRef={inputRef}
          setQuery={setQuery}
          query={query}
          floatingStyles={floatingStyles}
          refs={refs}
          id={props.id}
          onCreateOption={onCreateOption}
          onUpdateOption={onUpdateOption}
          onRemoveOption={onRemoveOption}
          name={name}
          options={options}
          onClickOutside={onClickOutside}
          openOnMount={props.openOnMount}
        />
      </Combobox>
    </div>
  );
}
